import { useCallback } from 'react';
import { toast } from 'react-toastify';
import type { SearchResultItem } from '@hopdrive/feature-global-search';
import { useRooftop, useRooftops } from '@store';
import { applyRooftopSwitch } from '@features/layout/utils/switchRooftop';

/**
 * Click handler for search results: when a result belongs to another rooftop
 * the user has access to (it's in their header switcher list), switch the
 * active rooftop — exactly as if picked in the header — before the link
 * navigates, and say so with a toast.
 *
 * Deliberate non-switches: same-rooftop clicks; results whose rooftop is not
 * in the switcher list (plain dealers have no list — their context stays put
 * and the card's rooftop chip carries the context); and modified clicks
 * (cmd/ctrl/shift/alt open new tabs — this tab's context must not change).
 */
export function useSearchRooftopSwitch(): (item: SearchResultItem, event: React.MouseEvent<HTMLElement>) => void {
  const rooftop = useRooftop();
  const rooftops = useRooftops();

  return useCallback(
    (item, event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!item.customerId || item.customerId === rooftop?.id) return;
      const target = rooftops.find(r => r?.id === item.customerId);
      if (!target) return;
      applyRooftopSwitch(target);
      toast.success(`Switched to ${target.name ?? 'the result’s rooftop'}`);
    },
    [rooftop?.id, rooftops]
  );
}
